import type { Translation } from '../types.ts';
import type en from '../en/tax.ts';

export default {
    settings: {
        title: 'Btw',
        intro: 'De gegevens waarop de btw-engine beslist, zoals jij of je belastingadviseur ze hebt bevestigd. De engine vult nooit zelf een ontbrekend gegeven in: een bestelling waarover hij niet kan beslissen, gaat naar controle.',
        modes: {
            originEligible: 'Nederlandse btw: onder de EU-drempel van € 10.000, geen keuze gemaakt',
            destinationRequired: 'Btw van het land van bestemming: drempel overschreden',
            destinationElected: 'Btw van het land van bestemming: gekozen (2 jaar bindend)',
        },
        consumerVat: {
            nl: 'Nederlandse btw: onze EU-verkopen aan consumenten blijven onder € 10.000 per jaar',
            dest: 'De btw van hun eigen land: boven € 10.000, of we hebben daarvoor gekozen',
            unconfirmed: 'Nog niet bevestigd',
        },
        destinationReason: {
            required: 'We zitten boven de drempel van € 10.000',
            elected: 'We hebben ervoor gekozen (2 jaar bindend)',
        },
        policy: {
            keepGross: 'De prijs op de site (je marge vangt het verschil in btw-tarief op)',
            keepNet: 'De prijs met het btw-tarief van hun land',
        },
        toasts: {
            draftStarted: 'Concept gestart',
            draftSaved: 'Concept opgeslagen',
            approved: 'Profiel goedgekeurd',
            engineOn: 'Btw-engine aan',
            engineOff: 'Btw-engine uit',
            draftDiscarded: 'Concept weggegooid',
        },
        confirmApprove: 'Dit profiel goedkeuren? Vanaf nu bepaalt het hoe de verkopen van deze winkel worden belast, en het kan daarna niet meer worden gewijzigd.',
        confirmEngineOn: 'De btw-engine aanzetten? Vanaf nu worden bestellingen berekend en getoond zoals de engine beslist: btw na de kortingscode, btw over de verzending en bedragen incl. btw op bonnen. Een bestelling waarover hij niet kan beslissen, wordt geweigerd.',
        confirmEngineOff: 'De btw-engine uitzetten? Nieuwe bestellingen worden weer per product belast.',
        confirmDiscard: 'Dit concept weggooien?',
        engine: {
            title: 'Engine',
            onText: 'Bestellingen worden door de btw-engine belast en incl. btw getoond.',
            offText: 'Bestellingen worden per product belast, zoals voorheen.',
            approveFirst: 'Keur eerst een profiel goed',
            switchOff: 'Uitzetten',
            switchOn: 'Aanzetten',
        },
        inForce: {
            title: 'Geldend profiel',
            none: 'Nog geen goedgekeurd profiel. Begin er hieronder een; zonder profiel kan de engine geen bestelling belasten.',
            version: 'Versie',
            versionValue: '{version}, vanaf {date}',
            seller: 'Verkoper',
            sellerValue: '{name}, verzendt vanuit {country}',
            vatNumbers: 'Btw-nummers',
            consumersAbroad: 'Consumenten in het buitenland',
            notConfirmed: 'Niet bevestigd',
            modeFrom: '(vanaf {from})',
            modeFromUntil: '(van {from} tot {until})',
            declaredThrough: 'Aangegeven via',
            oss: 'OSS-aangifte ({country}) vanaf {date}',
            local: 'Lokale registratie: {countries}',
            dutchOnly: 'Alleen de Nederlandse btw-aangifte',
            approved: 'Goedgekeurd',
            approvedBy: '{date} door {name}',
            change: 'Profiel wijzigen',
            start: 'Profiel beginnen',
        },
        draft: {
            title: 'Conceptversie {version}',
            sellerName: 'Juridische naam van de verkoper',
            sellerNameHint: 'Zoals op de factuur, bijv. Winkel B.V.',
            appliesFrom: 'Geldt vanaf',
            appliesFromHint: 'Nooit eerder dan het moment van goedkeuren.',
            vatNumbers: 'Je btw-nummers',
            vatNumbersHint: 'Je Nederlandse btw-nummer. Het staat op facturen en is nodig voor intracommunautaire leveringen tegen 0% aan bedrijven in andere EU-landen.',
            addVatNumber: 'Btw-nummer toevoegen',
            consumerVat: 'Waar betalen consumenten in andere EU-landen btw?',
            consumerVatHint: 'Onder € 10.000 per jaar aan verkopen aan consumenten in andere EU-landen (alle landen samen, excl. btw, dit jaar en vorig jaar) reken je Nederlandse btw. Tel al je verkoopkanalen mee, niet alleen deze winkel, en laat het bevestigen door je adviseur.',
            unconfirmedWarning: 'Zolang dit niet is bevestigd, weigert het afrekenen bestellingen van consumenten in andere EU-landen. Nederlandse bestellingen en bedrijven met een geldig btw-nummer merken er niets van.',
            modeSummary: 'Vanaf {date} (wijzigen)',
            modeSummaryReference: 'Vanaf {date}, {reference} (wijzigen)',
            from: 'Vanaf',
            untilOptional: 'Tot (optioneel)',
            referenceOptional: 'Kenmerk (optioneel)',
            referencePlaceholder: 'Brief van de adviseur',
            why: 'Waarom',
            reference: 'Kenmerk',
            referencePlaceholderDestination: 'Brief van de adviseur, keuze',
            howDeclare: 'Hoe je het aangeeft',
            howDeclareHint: 'Via een OSS-aangifte (het EU-éénloketsysteem, ingediend bij de Nederlandse Belastingdienst), of via een btw-registratie in het land van de klant.',
            ossCountry: 'OSS-registratie in',
            ossCountryPlaceholder: 'NL (leeg: niet geregistreerd)',
            ossFrom: 'OSS vanaf',
            addLocal: 'Lokale registratie toevoegen',
            policy: 'Wat een klant in het buitenland betaalt',
            save: 'Concept opslaan',
            approve: 'Goedkeuren',
            discard: 'Weggooien',
        },
        history: {
            title: 'Geschiedenis',
            version: 'Versie',
            from: 'Vanaf',
            approved: 'Goedgekeurd',
            inForce: 'geldend',
        },
    },
    report: {
        title: 'Btw-aangifte',
        period: 'Periode',
        quarter: 'K{quarter} {year}',
        quarterRunning: 'K{quarter} {year} (lopend)',
        perProduct: '{n} bestelling in deze periode is per product belast, niet door de btw-engine, en staat niet in deze cijfers. | {n} bestellingen in deze periode zijn per product belast, niet door de btw-engine, en staan niet in deze cijfers.',
        empty: 'Geen verkopen door de btw-engine belast in deze periode.',
        nlReturn: 'Nederlandse btw-aangifte',
        box: 'Rubriek',
        base: 'Grondslag',
        vat: 'Btw',
        oss: 'OSS-aangifte (btw van het land van bestemming)',
        local: 'Lokale btw-registraties',
        rate: 'Tarief',
        icp: 'Opgaaf intracommunautaire prestaties (ICP)',
        icpHint: 'Intracommunautaire leveringen tegen 0% aan bedrijven in andere EU-landen, per btw-nummer van de koper.',
        vatNumber: 'Btw-nummer',
        giftCards: 'Verkochte cadeaubonnen: {amount}, buiten de btw en nergens aangegeven.',
    },
    evidence: {
        title: 'Vervoersbewijs',
        pieces: {
            vies: 'VIES-controle',
            invoice: 'Factuur',
            tracking: 'Trackingcode',
            delivery: 'Afleverbevestiging',
        },
        status: {
            complete: 'Bewijs compleet',
            pending: 'Nog niet verzonden',
            missing: 'Bewijs ontbreekt',
            exception: 'Als uitzondering geaccepteerd',
            notRequired: 'Geen bewijs nodig',
        },
        panel: {
            missingCount: '{n} ontbreekt | {n} ontbreken',
            intro: 'Intracommunautaire leveringen tegen 0% aan bedrijven in andere EU-landen. Voor elke levering moet het orderdossier bewijzen dat de goederen Nederland hebben verlaten.',
            order: 'Bestelling',
            buyer: 'Koper',
            proof: 'Bewijs',
            shipped: 'verzonden {date}',
            paid: 'betaald {date}',
            missing: 'Ontbreekt: {pieces}',
        },
        card: {
            intro: 'Tegen 0% verkocht aan een bedrijf in een ander EU-land (intracommunautaire levering). Het orderdossier moet aantonen dat de goederen Nederland hebben verlaten.',
            deliveryRecorded: 'Aflevering vastgelegd',
            deliveryRemoved: 'Afleverbevestiging verwijderd',
            fileAdded: 'Bestand toegevoegd',
            fileRemoved: 'Bestand verwijderd',
            exceptionAccepted: 'Als uitzondering geaccepteerd',
            exceptionWithdrawn: 'Uitzondering ingetrokken',
            confirmClearDelivery: 'De afleverbevestiging verwijderen?',
            confirmRemoveFile: '{name} verwijderen?',
            theFile: 'het bestand',
            confirmWithdraw: 'De uitzondering intrekken? Het bewijs telt dan weer als ontbrekend.',
            vies: '{number}: {result}, {date}',
            viesConsultation: '{number}: {result}, {date}, raadplegingsnummer {consultation}',
            noVies: 'Geen VIES-controle bij deze bestelling',
            invoice: '{number}, {date}',
            noInvoice: 'Verstuur de factuur vanaf het tabblad Factuur',
            noTracking: 'Voeg hem toe onder Verzending',
            delivered: 'Afgeleverd {date}',
            deliveredReference: 'Afgeleverd {date}, {reference}',
            noDelivery: 'Leg het hieronder vast zodra de vervoerder de aflevering bevestigt',
            deliveredLine: 'Afgeleverd {date}.',
            deliveredLineReference: 'Afgeleverd {date}, kenmerk vervoerder {reference}.',
            recordedBy: 'Vastgelegd door {name} {date}',
            change: 'Wijzigen',
            deliveryConfirmation: 'Afleverbevestiging',
            deliveredOn: 'Afgeleverd op',
            carrierReference: 'Kenmerk vervoerder',
            carrierReferencePlaceholder: 'Track & trace, CMR-nummer',
            note: 'Notitie (optioneel)',
            notePlaceholder: 'Getekend door …',
            recordDelivery: 'Aflevering vastleggen',
            proofFile: 'Bewijsbestand',
            optional: '(optioneel)',
            replace: 'Vervangen',
            addFile: 'Bestand toevoegen',
            fileHint: 'Het afleverbewijs van de vervoerder of een getekende CMR. PDF, JPG of PNG, maximaal 10 MB.',
            acceptedBy: 'Zonder volledig bewijs geaccepteerd door {name} {date}:',
            withdraw: 'Uitzondering intrekken',
            acceptWithout: 'Accepteren zonder volledig bewijs…',
            why: 'Waarom wordt deze verkoop zonder volledig bewijs geaccepteerd?',
            accept: 'Als uitzondering accepteren',
        },
    },
    table: {
        title: 'Btw-tabel',
        intro: 'Btw-categorieën en het btw-tarief dat elk land ervoor rekent, voor alle winkels. Een regel wordt pas gerekend als iemand anders dan de maker hem heeft goedgekeurd.',
        treatments: {
            standard: 'Standaard',
            reduced: 'Verlaagd',
            zeroRated: 'Nultarief (0%)',
            exempt: 'Vrijgesteld',
        },
        treatmentShort: {
            standard: 'standaard',
            reduced: 'verlaagd',
            zeroRated: 'nultarief',
            exempt: 'vrijgesteld',
        },
        status: {
            draft: 'Wacht op goedkeuring',
            inForce: 'Geldend',
            scheduled: 'Gepland',
            ended: 'Beëindigd',
        },
        toasts: {
            categoryAdded: 'Categorie toegevoegd',
            ruleAdded: 'Regel toegevoegd; hij telt zodra iemand anders hem goedkeurt',
            ruleApproved: 'Regel goedgekeurd',
            ruleEnded: 'Regel beëindigd',
            draftDeleted: 'Concept verwijderd',
        },
        pickCategoryRate: 'Kies een categorie en een tarief.',
        confirmApprove: '{country} {category} tegen {rate} vanaf {date} goedkeuren? Het tarief wordt gerekend in elke winkel die deze categorie daar verkoopt.',
        promptEnd: 'Deze regel beëindigen op (JJJJ-MM-DD, de eerste dag dat hij niet meer geldt):',
        confirmDelete: 'Deze conceptregel verwijderen?',
        rules: {
            title: 'Regels',
            countryCategory: 'Land · categorie',
            rate: 'Tarief',
            from: 'Vanaf',
            until: 'Tot',
            source: 'Bron',
            open: 'open',
            by: 'door {name}',
            byApproved: 'door {name}, goedgekeurd door {approver}',
            migration: 'migratie',
            approve: 'Goedkeuren',
            end: 'Beëindigen',
            category: 'Categorie',
            treatment: 'Behandeling',
            ratePercent: 'Tarief %',
            sourcePlaceholder: 'Wetsartikel of officiële pagina',
            add: 'Regel toevoegen',
        },
        categories: {
            title: 'Categorieën',
            label: 'Naam',
            contents: 'Wat erin valt',
            codePlaceholder: 'food',
            labelPlaceholder: 'Voeding',
            add: 'Categorie toevoegen',
        },
    },
} satisfies Translation<typeof en>;
